import * as React from 'react';

import { cn } from '../../lib/cn';

/** Props di {@link Table}: nessuna prop custom, solo gli attributi HTML standard di `<table>`. */
export interface TableProps extends React.HTMLAttributes<HTMLTableElement> {}

/** Tabella standard dell'app, con contenitore scrollabile orizzontalmente. Componi con TableHeader/TableBody/TableRow/TableHead/TableCell. */
const Table = React.forwardRef<HTMLTableElement, TableProps>(({ className, ...props }, ref) => (
	<div className="relative w-full overflow-auto">
		<table ref={ref} className={cn('w-full caption-bottom text-[13px]', className)} {...props} />
	</div>
));
Table.displayName = 'Table';

/** Props di {@link TableHeader}: nessuna prop custom, solo gli attributi HTML standard di `<thead>`. */
export interface TableHeaderProps extends React.HTMLAttributes<HTMLTableSectionElement> {}

/** Intestazione di {@link Table}. */
const TableHeader = React.forwardRef<HTMLTableSectionElement, TableHeaderProps>(
	({ className, ...props }, ref) => (
		<thead
			ref={ref}
			className={cn('bg-[rgba(26,58,92,0.4)] [&_tr]:border-b [&_tr]:border-border/40', className)}
			{...props}
		/>
	),
);
TableHeader.displayName = 'TableHeader';

/** Props di {@link TableBody}: nessuna prop custom, solo gli attributi HTML standard di `<tbody>`. */
export interface TableBodyProps extends React.HTMLAttributes<HTMLTableSectionElement> {}

/** Corpo di {@link Table}. */
const TableBody = React.forwardRef<HTMLTableSectionElement, TableBodyProps>(
	({ className, ...props }, ref) => (
		<tbody ref={ref} className={cn('[&_tr:last-child]:border-0', className)} {...props} />
	),
);
TableBody.displayName = 'TableBody';

/** Props di {@link TableFooter}: nessuna prop custom, solo gli attributi HTML standard di `<tfoot>`. */
export interface TableFooterProps extends React.HTMLAttributes<HTMLTableSectionElement> {}

/** Piè di {@link Table} (es. riga di totale). */
const TableFooter = React.forwardRef<HTMLTableSectionElement, TableFooterProps>(
	({ className, ...props }, ref) => (
		<tfoot
			ref={ref}
			className={cn(
				'border-t border-accent-green/25 bg-accent-green/[0.06] font-medium [&>tr]:last:border-b-0',
				className,
			)}
			{...props}
		/>
	),
);
TableFooter.displayName = 'TableFooter';

/** Props di {@link TableRow}: nessuna prop custom, solo gli attributi HTML standard di `<tr>`. */
export interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {}

/** Riga di {@link Table}. */
const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>(
	({ className, ...props }, ref) => (
		<tr
			ref={ref}
			className={cn(
				'border-b border-border/40 transition-colors hover:bg-accent/[0.02] data-[state=selected]:bg-muted',
				className,
			)}
			{...props}
		/>
	),
);
TableRow.displayName = 'TableRow';

/** Props di {@link TableHead}: nessuna prop custom, solo gli attributi HTML standard di `<th>`. */
export interface TableHeadProps extends React.ThHTMLAttributes<HTMLTableCellElement> {}

/** Cella di intestazione di {@link Table}. */
const TableHead = React.forwardRef<HTMLTableCellElement, TableHeadProps>(
	({ className, ...props }, ref) => (
		<th
			ref={ref}
			className={cn(
				'px-2.5 py-[7px] text-left align-middle font-mono text-[11px] uppercase tracking-[1px] text-muted-foreground [&:has([role=checkbox])]:pr-0',
				className,
			)}
			{...props}
		/>
	),
);
TableHead.displayName = 'TableHead';

/** Props di {@link TableCell}: nessuna prop custom, solo gli attributi HTML standard di `<td>`. */
export interface TableCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {}

/** Cella di dati di {@link Table}. */
const TableCell = React.forwardRef<HTMLTableCellElement, TableCellProps>(
	({ className, ...props }, ref) => (
		<td
			ref={ref}
			className={cn('px-2.5 py-2 align-middle [&:has([role=checkbox])]:pr-0', className)}
			{...props}
		/>
	),
);
TableCell.displayName = 'TableCell';

/** Props di {@link TableCaption}: nessuna prop custom, solo gli attributi HTML standard di `<caption>`. */
export interface TableCaptionProps extends React.HTMLAttributes<HTMLTableCaptionElement> {}

/** Didascalia di {@link Table}. */
const TableCaption = React.forwardRef<HTMLTableCaptionElement, TableCaptionProps>(
	({ className, ...props }, ref) => (
		<caption
			ref={ref}
			className={cn('mt-4 text-[12px] text-muted-foreground', className)}
			{...props}
		/>
	),
);
TableCaption.displayName = 'TableCaption';

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption };
